import { atom } from 'jotai'
import React from 'react'

import { type OutputType } from '~/gql/graphql'
import { exists, type FigureInfo, type Group } from '~/utils'

import { GroupCountsFiguresContent } from './components/GroupCountsFiguresContent'
import { AtomsProvider } from './providers/AtomsProvider'
import { CommentGenerationProvider } from './providers/CommentGenerationProvider'
import { CreateFigureInfoProvider } from './providers/CreateFigureInfoProvider'
import { CreateRestoringParamsProvider } from './providers/CreateRestoringParamsProvider'
import {
  type CommentAtom,
  type CustomerAttributeQueryDataAtom,
  type DataDisplayTypeAtom,
  type GroupDefinitionMap,
  type GroupNameMapAtom,
  type GroupsDiffsAtom,
  type HiddenColumnKeysAtom,
  type HiddenRowIdsAtom,
  type IndividualSortValueAtom,
  type MatrixSortValueAtom,
  type QuestionQueryDataAtom,
  type QuestionsDiffsMapAtom,
  type ResultViewerTypeAtom,
  type SequentialSortValueAtom,
} from './utils'

export type GroupCountsFiguresProps = {
  commentAtom: CommentAtom
  createFigureInfo: (figureType: string) => FigureInfo
  createRestoringParams: (hash: string) => unknown
  customerAttributeQueryDataAtom: CustomerAttributeQueryDataAtom
  dataDisplayTypeAtom: DataDisplayTypeAtom
  groupDefinitionMap: GroupDefinitionMap
  groupNameMapAtom: GroupNameMapAtom
  groupsDiffsAtom: GroupsDiffsAtom
  hiddenColumnKeysAtom: HiddenColumnKeysAtom
  hiddenRowIdsAtom: HiddenRowIdsAtom
  individualSortValueAtom: IndividualSortValueAtom
  matrixSortValueAtom: MatrixSortValueAtom
  outputType: OutputType
  questionQueryDataAtom: QuestionQueryDataAtom
  questionsDiffsMapAtom: QuestionsDiffsMapAtom
  resultViewerTypeAtom: ResultViewerTypeAtom
  savedCommentAtom: CommentAtom
  sequentialSortValueAtom: SequentialSortValueAtom
  surveyId: string
}

export const GroupCountsFigures = ({
  createFigureInfo,
  createRestoringParams,
  groupDefinitionMap,
  outputType,
  surveyId,
  ...atomProps
}: GroupCountsFiguresProps) => {
  const [nameEditingGroupAtom] = React.useState(atom<Group | null>(null))

  const [isTabulatedAtom] = React.useState(
    atom<boolean>(
      (get) =>
        exists(get(atomProps.questionQueryDataAtom)) &&
        exists(get(atomProps.customerAttributeQueryDataAtom)),
    ),
  )

  return (
    <AtomsProvider
      atoms={{ ...atomProps, isTabulatedAtom, nameEditingGroupAtom }}
    >
      <CreateRestoringParamsProvider
        createRestoringParams={createRestoringParams}
      >
        <CreateFigureInfoProvider createFigureInfo={createFigureInfo}>
          <CommentGenerationProvider
            groupDefinitionMap={groupDefinitionMap}
            outputType={outputType}
            surveyId={surveyId}
          >
            <GroupCountsFiguresContent />
          </CommentGenerationProvider>
        </CreateFigureInfoProvider>
      </CreateRestoringParamsProvider>
    </AtomsProvider>
  )
}
